import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { message } from "antd";
import PhaserView, { PhaserRef } from "../Phaser";
import BlocklyWorkspace, {
  BlocklyWorkspaceRef,
} from "../Blockly/BlocklyWorkspace";
import {
  BlocklyConfig,
  getDefaultConfigWorkspace,
} from "../Blockly/types/config";
import { getDefaultConfigPhaser } from "../Phaser/types/config";
import { EventManager } from "../Phaser/engine/events/EventManager";
import {
  createCodeInterpreter,
  CodeInterpreter,
} from "../BlocklyPhaser/services/codeInterpreter";
import {
  ExecutionProvider,
  useExecutionContext,
} from "../BlocklyPhaser/contexts/ExecutionContext";
import DebugTB from "../BlocklyPhaser/toolbars/DebugTB";
import ResponsiveBlocklyPhaserLayout from "../BlocklyPhaser/ResponsiveBlocklyPhaserLayout";
import { useObjectsApi } from "../../api/objects/objects";
import { useProjectApi } from "../../api/projects/projects";
import { TProject } from "../../models/project/project";
import { TSprite } from "../../models/objects/sprite";
import { TObstacle, TScene } from "../../models/objects/objects";
import { TSound } from "../../models/objects/media";
import { defineProjectBlocks } from "../Blockly/BlocklyWorkspace/blocks/project";
import { buildProjectToolbox } from "./projectToolbox";
import { buildSpriteThumbnails } from "./spriteThumbnails";

interface BlocklyPhaserProjectProps {
  project: TProject;
  enableEditCode: boolean;
}
export interface BlocklyPhaserProjectRef {
  save: () => Promise<void>;
}

const BlocklyPhaserProjectContent = forwardRef<
  BlocklyPhaserProjectRef,
  BlocklyPhaserProjectProps
>((props, ref) => {
  const { getSpritesDetails, getScenes, getObstacles, getSounds } =
    useObjectsApi();
  const { updateProject } = useProjectApi();
  const {
    isRunning,
    isPaused,
    start,
    restart,
    stepBystepOn,
    stepBystepOff,
    nextStep,
    addLog,
    stop,
  } = useExecutionContext();
  const [sprites, setSprites] = useState<TSprite[]>([]);
  const [dbScenes, setDbScenes] = useState<TScene[]>([]);
  const [dbObstacles, setDbObstacles] = useState<TObstacle[]>([]);
  const [sounds, setSounds] = useState<TSound[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const workspaceRef = useRef<BlocklyWorkspaceRef>(null);
  const phaserRef = useRef<PhaserRef>(null);
  const eventManagerRef = useRef<EventManager | null>(null);
  const interpreterRef = useRef<CodeInterpreter | null>(null);
  const workspaceXmlRef = useRef(props.project.config?.workspace ?? "");
  const configPhaser = useMemo(() => getDefaultConfigPhaser(), []);
  const workspaceConfig = useMemo<BlocklyConfig>(
    () => ({
      ...getDefaultConfigWorkspace(false),
      toolboxGrouped: true,
      toolboxCategories: buildProjectToolbox(props.enableEditCode),
      workspace: workspaceXmlRef.current,
    }),
    [props.enableEditCode],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getSpritesDetails(), getScenes(), getObstacles(), getSounds()])
      .then(async ([characterData, sceneData, obstacleData, soundData]) => {
        if (!active) return;
        const chars = characterData.data;
        const scenes = sceneData.data;
        const obstacles = obstacleData.data;
        setSprites(chars);
        setDbScenes(scenes);
        setDbObstacles(obstacles);
        setSounds(soundData.data);
        const thumbs = await buildSpriteThumbnails(chars);
        defineProjectBlocks({
          sprites: chars,
          scenes,
          obstacles,
          spriteThumbnails: thumbs,
        });
      })
      .catch((err) => {
        setLoadError(true);
        message.error(err.response?.data ?? err.message ?? err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    }; // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const interpreter = createCodeInterpreter({
      isRunning,
      isPaused,
      currentStep: 0,
      nextStep,
      addLog,
      stop,
    });
    interpreterRef.current = interpreter;
    const eventManager = new EventManager();
    eventManagerRef.current = eventManager;
    (window as any).eventManager = eventManager;
    const keyDown = (event: KeyboardEvent) =>
      eventManager.dispatchKeyPressEvent(event.key);
    document.addEventListener("keydown", keyDown);
    return () => {
      document.removeEventListener("keydown", keyDown);
      eventManager.clearAll();
      if (eventManagerRef.current === eventManager)
        eventManagerRef.current = null;
      if ((window as any).eventManager === eventManager)
        delete (window as any).eventManager;
    }; // eslint-disable-next-line
  }, []);
  useEffect(() => {
    interpreterRef.current?.setPaused(isPaused);
  }, [isPaused]);
  useEffect(() => {
    interpreterRef.current?.setRunning(isRunning);
  }, [isRunning]);
  useEffect(() => {
    if (!loading) start();
  }, [loading, start]);

  const save = async () => {
    if (loadError) return;
    try {
      const xml =
        workspaceRef.current?.getWorkspaceXml() ?? workspaceXmlRef.current;
      workspaceXmlRef.current = xml;
      await updateProject(props.project.id, {
        ...props.project,
        config: { workspace: xml },
      });
    } catch (err: any) {
      message.error(err.response?.data ?? err.message ?? err);
    }
  };
  useImperativeHandle(ref, () => ({ save }));
  const handleRun = async () => {
    eventManagerRef.current?.clearAllCollisionListeners();
    stepBystepOff();
    workspaceXmlRef.current =
      workspaceRef.current?.getWorkspaceXml() ?? workspaceXmlRef.current;
    if (!workspaceRef.current || !interpreterRef.current) return;
    interpreterRef.current.init(workspaceRef.current.getCode());
    start();
    try {
      await interpreterRef.current.runAll();
    } catch (err) {
      console.error("Execution error:", err);
    }
  };
  const handleNextStep = () => {
    stepBystepOn();
    if (!isRunning) start();
    if (!workspaceRef.current || !interpreterRef.current) return;
    if (interpreterRef.current.hasMore()) interpreterRef.current.step();
    else {
      eventManagerRef.current?.clearAllCollisionListeners();
      interpreterRef.current.init(workspaceRef.current.getCode());
      interpreterRef.current.step();
    }
  };
  const handleRestart = async () => {
    eventManagerRef.current?.clearAllCollisionListeners();
    await phaserRef.current?.handleResetConfig();
    restart();
    if (props.enableEditCode) await save();
  };
  const onWin = () => message.success("🎉 Bravo! Vous avez gagné!", 5);
  const blocklyComponent = (
    <div
      className="tilmi-blockly-panel"
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <BlocklyWorkspace
        ref={workspaceRef}
        config={workspaceConfig}
        configPhaser={configPhaser}
        height="60vh"
      />
      <div
        style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}
      >
        <DebugTB
          onRun={handleRun}
          onNextStep={handleNextStep}
          handleRestart={handleRestart}
        />
      </div>
    </div>
  );
  const phaserComponent = (
    <div style={{ width: "100%", minWidth: 0, position: "relative" }}>
      <PhaserView
        projectMode={true}
        adminMode={false}
        ref={phaserRef}
        eventManager={eventManagerRef.current}
        config={configPhaser}
        isRunning={true}
        onWin={onWin}
        sprites={sprites}
        scenesConfig={dbScenes}
        obstacles={dbObstacles}
        textsConfig={[]}
        sounds={sounds}
      />
    </div>
  );
  return (
    <div style={{ width: "100%", height: "100%" }}>
      {!loading && !loadError && eventManagerRef.current && (
        <ResponsiveBlocklyPhaserLayout
          className="tilmi-blockly-container-shell"
          blocklyComponent={blocklyComponent}
          phaserComponent={phaserComponent}
        />
      )}
    </div>
  );
});

const BlocklyPhaserProject = forwardRef<
  BlocklyPhaserProjectRef,
  BlocklyPhaserProjectProps
>((props, ref) => (
  <ExecutionProvider>
    <BlocklyPhaserProjectContent ref={ref} {...props} />
  </ExecutionProvider>
));
export default BlocklyPhaserProject;
